import { InjectionToken } from '@angular/core';
import { Environment } from '@galaxy/core';
import { Observable } from 'rxjs';

export const PartnerIdToken = new InjectionToken<Observable<string>>('PARTNER_ID');

export const GET_VENDOR_APP_NAMES_INSTEAD_OF_WHITELABELED_NAMES = 'orwl-phulldan9er0u5vend0rname';
export const LISTING_DISTRIBUTION_ADDON_IDS_PROD = ['A-GMXXNQ4ZGD', 'A-SX5MP2FB2L'];
export const LISTING_DISTRIBUTION_ADDON_IDS_DEMO = ['A-TWFC53JDQX', 'A-FR5P5ND7KN'];

const websiteProAppIDs = {
  [Environment.DEMO]: 'MP-9cc9f21f0a234a46ad78087fc09f16bc',
  [Environment.PROD]: 'MP-ee4ea04e553a4b1780caf7aad7be07cd',
};
const customerVoiceAppIDs = {
  [Environment.DEMO]: 'MP-fba21121b71148c9bb33e11fcd92d520',
  [Environment.PROD]: 'MP-c4974d390a044c28aec31e421aa662b2',
};
const calendarHeroAppIDs = {
  [Environment.DEMO]: 'MP-M2R4V4KZH4QLSRM3ZZ6DQB7N8Z6PL5NL',
  [Environment.PROD]: 'MP-58G85F84DZBND7CK228578MWLD38SS4S',
};

// Owned and operated App IDs
export const AppIDs = {
  ReputationManagement: 'RM', // Same for demo and prod
  SocialMarketing: 'SM', // Same for demo and prod
  ListingBuilder: 'MS', // Same for demo and prod
  AdvertisingIntelligence: 'MP-94072e44d5364872b672d7ab4fc7a7e8', // Same for demo and prod
  WebsiteAppIDs: websiteProAppIDs,
  CustomerVoiceAppIDs: customerVoiceAppIDs,
  CalendarHeroAppIDs: calendarHeroAppIDs,

  CustomerVoice: 'MP-c4974d390a044c28aec31e421aa662b2', // deprecated, use CustomerVoiceAppIDs instead
  Website: 'MP-ee4ea04e553a4b1780caf7aad7be07cd', // deprecated, use WebsiteAppIDs instead
  WebsiteDemo: 'MP-9cc9f21f0a234a46ad78087fc09f16bc', // deprecated, use WebsiteAppIDs instead
};

// Owned and operated edition IDs
export const EditionIDs = {
  [Environment.DEMO]: {
    ReputationManagement: { Express: 'EDITION-38SMW45H' },
    SocialMarketing: { Express: 'EDITION-SWVF3WH8' },
    CustomerVoice: { Express: 'EDITION-4WWZC3RJ' },
    Website: { Express: 'EDITION-RC58KN73' },
  },
  [Environment.PROD]: {
    ReputationManagement: { Express: 'EDITION-F7JZ5TV8' },
    SocialMarketing: { Express: 'EDITION-FVGBNLVZ' },
    CustomerVoice: { Express: 'EDITION-TC8HJZNS' },
    Website: { Express: 'EDITION-VFNL43ZF' },
  },
};

export interface PreviewEmailText {
  paragraph1: string;
  paragraph2: string;
  paragraph3_CZ?: string;
  paragraph3?: string;
  paragraph4: string;
  paragraph5: string;
  paragraph6: string;
  paragraph7: string;
}

export const WELCOME_EMAIL_LOCALES = [
  {
    id: 'en',
    name: 'English',
  },
  {
    id: 'cs',
    name: 'Čeština',
  },
  {
    id: 'de',
    name: 'Deutsch',
  },
  {
    id: 'es-419',
    name: 'Español',
  },
  {
    id: 'fr',
    name: 'Français',
  },
  {
    id: 'it',
    name: 'Italiano',
  },
  {
    id: 'nl',
    name: 'Nederlands',
  },
  {
    id: 'pt-br',
    name: 'Português',
  },
  {
    id: 'ru',
    name: 'Русский',
  },
];

export type EmailLocale =
  | {
      value: 'en';
      label: 'English';
    }
  | {
      value: 'cs';
      label: 'Čeština';
    }
  | {
      value: 'de';
      label: 'Deutsch';
    }
  | {
      value: 'es-419';
      label: 'Español';
    }
  | {
      value: 'fr';
      label: 'Français';
    }
  | {
      value: 'it';
      label: 'Italiano';
    }
  | {
      value: 'nl';
      label: 'Nederlands';
    }
  | {
      value: 'pt-br';
      label: 'Português';
    }
  | {
      value: 'ru';
      label: 'Русский';
    };

export const SELECT_WELCOME_EMAIL_LOCALES: EmailLocale[] = [
  {
    value: 'en',
    label: 'English',
  },
  {
    value: 'cs',
    label: 'Čeština',
  },
  {
    value: 'de',
    label: 'Deutsch',
  },
  {
    value: 'es-419',
    label: 'Español',
  },
  {
    value: 'fr',
    label: 'Français',
  },
  {
    value: 'it',
    label: 'Italiano',
  },
  {
    value: 'nl',
    label: 'Nederlands',
  },
  {
    value: 'pt-br',
    label: 'Português',
  },
  {
    value: 'ru',
    label: 'Русский',
  },
];

export const SELECT_WELCOME_EMAIL_LOCALES_HASH: Record<string, PreviewEmailText> = {
  en: {
    paragraph1: 'Welcome to',
    paragraph2: 'Hi',
    paragraph3: 'The',
    paragraph4: 'is your gateway to access and manage all your digital solutions.',
    paragraph5:
      'Click the button below to create your password and sign in. After you have set your password and signed in you will be able to access and view your digital solutions.',
    paragraph6: 'Set your password',
    paragraph7: 'This password reset is one-time use. You can access Business App at any time using the following URL:',
  },
  cs: {
    paragraph1: 'Vítá vás',
    paragraph2: 'Dobrý den',
    paragraph3_CZ: 'společnosti',
    paragraph4: 'je bránou pro přístup a správu vašich online nástrojů.',
    paragraph5:
      'Kliknutím na tlačítko níže vytvoříte své heslo a přihlásíte se. Po nastavení hesla a přihlášení budete mít přístup k vašim online nástrojům.',
    paragraph6: 'Nastavte své heslo',
    paragraph7:
      'Toto resetování hesla je jednorázové. K produktu Business App máte přístup kdykoliv na následující adrese:',
  },
  de: {
    paragraph1: 'Willkommen in der',
    paragraph2: 'Hallo',
    paragraph3: 'Die',
    paragraph4: 'ist der zentrale und einheitliche Zugang zu allen integrierten digitalen Anwendungen.',
    paragraph5:
      'Bitte klicken Sie auf die Schaltfläche "Passwort erstellen" unten, um Ihr Passwort zu erstellen und sich anzumelden.',
    paragraph6: 'Passwort erstellen',
    paragraph7:
      'Diese eMail zum Zurücksetzen des Passworts dient der einmaligen Anwendung. Sie können danach jederzeit über den folgenden Link darauf Business App zugreifen',
  },
  'es-419': {
    paragraph1: 'Bienvenido al',
    paragraph2: 'Hola',
    paragraph3: 'El',
    paragraph4: 'es su puerta de entrada para acceder y gestionar todas sus soluciones digitales.',
    paragraph5:
      'Haz clic en el botón de abajo para crear tu contraseña e iniciar sesión. Una vez que haya establecido su contraseña y haya iniciado sesión, podrá acceder y ver sus soluciones digitales.',
    paragraph6: 'Establezca su contraseña',
    paragraph7:
      'Este restablecimiento de contraseña es de un solo uso. Puede acceder a Business App en cualquier momento utilizando la siguiente URL:',
  },
  fr: {
    paragraph1: 'Bienvenue chez',
    paragraph2: 'Bonjour',
    paragraph3: 'Le',
    paragraph4: 'est la passerellle pour accéder à vos solutions numériques et les gérer.',
    paragraph5:
      'Cliquez sur le bouton ci-dessous pour créer votre mot de passe et vous identifier. Quand vous aurez choisi votre mot de passe et que vous vous serez inscrit, vous pourrez accéder à vos solutions numériques et les consulter.',
    paragraph6: 'Définir votre mot de passe',
    paragraph7:
      "Cette réinitialisation du mot de passe est à usage unique. Vous pouvez accéder à Business App à tout moment en utilisant l'URL suivante:",
  },
  it: {
    paragraph1: 'Benvenuto in',
    paragraph2: 'Ciao',
    paragraph3: 'Il',
    paragraph4: 'è il tuo gateway per accedere e gestire tutte le tue soluzioni digitali.',
    paragraph5:
      "Clicca il pulsante qui sotto per creare la tua password e accedere. Dopo aver impostato la tua password e aver effettuato l'accesso, sarai in grado di accedere e visualizzare le tue soluzioni digitali.",
    paragraph6: 'Imposta la tua password',
    paragraph7:
      'Questa reimpostazione della password è utilizzabile una sola volta. Puoi accedere a Business App in qualsiasi momento utilizzando il seguente URL:',
  },
  nl: {
    paragraph1: 'Welkom bij Le',
    paragraph2: 'Hoi',
    paragraph3: 'Dit',
    paragraph4: 'is uw toegangspoort tot uw digitale oplossingen.',
    paragraph5:
      'Klik op de onderstaande knop om uw paswoord aan te maken en meld u aan. Eens u uw paswoord hebt aangemaakt hebt u toegang tot de digitale oplossingen.',
    paragraph6: 'Stel uw wachtwoord in',
    paragraph7:
      'Deze wachtwoord reset is eenmalig te gebruiken. U kunt op elk moment toegang krijgen tot Le Business Center via de volgende URL:',
  },
  'pt-br': {
    paragraph1: 'Bem-vindo ao',
    paragraph2: 'Olá',
    paragraph3: 'O',
    paragraph4: 'é a sua gateway para acessar e gerenciar todas as suas soluções digitais.',
    paragraph5:
      'Clique no botão abaixo para criar sua senha e fazer o login. Depois de definir a sua palavra-passe e iniciar sessão, poderá aceder e visualizar as suas soluções digitais.',
    paragraph6: 'Defina a sua senha ',
    paragraph7: 'Esta senha é de uso único. Você pode acessar Business App a qualquer momento usando a URL a seguir:',
  },
  ru: {
    paragraph1: 'Добро пожаловать в',
    paragraph2: 'Привет',
    paragraph3: '',
    paragraph4: '- это ваш шлюз для доступа и управления всеми цифровыми решениями.',
    paragraph5:
      'Нажмите кнопку ниже, чтобы создать свой пароль и войти в систему. После того, как вы установите пароль и войдете в систему, вы сможете получить доступ и просмотреть свои цифровые решения.',
    paragraph6: 'Установите пароль',
    paragraph7:
      'Этот сброс пароля является одноразовым. Вы можете получить доступ к Business App в любое время, используя следующий URL:',
  },
};

export const BULK_USER_IMPORT_CSV = [
  'First Name,Last Name,Email (Required),Phone,Account Identifier,Language Code,Greeting Name',
  'FAKE-NAME,FAKE-NAME,fake@email.com,123-456-7890,AG-01A34B678C,en-US,',
].join('\n');

export const WEBLATE_COMPONENT_NAME = 'partner-center/partner-center-client';

export const ALL_MARKETS_DEFAULT = '_DEFAULT';
